import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { z } from 'zod';

import type { CardModalPayload, ModalProps } from '../types/modal.types';

import { useGetUserQuery, useMarkPhysicalCardActivatedMutation } from '../../__generated__/graphql/api';
import { getUserRefetchQuery } from '../../components/UserDetails/getUserRefetchQuery';
import { createFormComponent } from '../utils/create-form-component';

const schema = z.object({
  referenceId: z.string().min(1, 'Reference ID is required'),
});

export const MarkPhysicalCardActivatedComponent = ({ payload, api }: ModalProps<CardModalPayload>) => {
  const { userId = '', cardReferenceId } = payload;

  const { data } = useGetUserQuery({
    variables: {
      userId,
    },
    skip: !userId || !!cardReferenceId,
  });

  const { handleMutation } = useMutationWithToast();
  const refetchQuery = getUserRefetchQuery(userId);
  const [markPhysicalCardActivated, { loading }] = useMarkPhysicalCardActivatedMutation(refetchQuery);

  const handleSubmit = (data: z.infer<typeof schema>) => {
    void handleMutation({
      mutation: () =>
        markPhysicalCardActivated({
          variables: {
            referenceId: data.referenceId,
          },
        }),
      extractSuccess: (result) => !!result?.data,
      onSuccess: () => api.close(),
      debugLabel: 'mark-physical-card-activated',
      toast: {
        success: 'Physical card marked as activated successfully',
        error: 'Failed to mark physical card as activated',
        loading: 'Marking card as activated...',
      },
    });
  };

  const user = data?.user;
  const activeCard = user?.cards?.find((card) => card?.active);
  const referenceId = cardReferenceId ?? activeCard?.referenceId ?? '';

  const config: SmartFormConfig<z.infer<typeof schema>> = {
    fields: [
      cardReferenceId
        ? {
            name: 'referenceId',
            label: '',
            type: 'text',
            defaultValue: referenceId,
            componentProps: {
              wrapperClassName: 'hidden',
              inputClassName: 'hidden',
            },
          }
        : {
            name: 'referenceId',
            label: 'Card Reference ID',
            type: 'select',
            required: true,
            options:
              user?.cards?.map((card) => ({
                value: card?.referenceId ?? '',
                label: card?.referenceId ?? '',
              })) ?? [],
          },
    ],
    schema,
    defaultValues: {
      referenceId,
    },
  };

  return createFormComponent({
    config,
    handleSubmit,
    loading,
  });
};
